import { useCallback, useMemo } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import type {
  AgentConversation,
  AgentMessage,
  AgentRun,
  AgentRunStatus,
  AssistantImageAttachment,
  DelegatedSendUndoHandle,
  ProposalDecisionRecord,
  UnifiedAttachment,
} from '@this+that/models';
import { AgentSubmitOrigin } from '@this+that/models';
import log from 'loglevel';

const EMPTY_LIST = Object.freeze([]) as never[];

export interface CreateEmailDraftInput {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  inReplyToMessageId?: string;
  threadId?: string;
  fromAddress?: string;
  attachments?: UnifiedAttachment[];
}

export interface SendEmailInput {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  inReplyToMessageId?: string;
  threadId?: string;
  fromAddress?: string;
  attachments?: UnifiedAttachment[];
}

export interface EmailDraftCreated {
  draftId: string;
  messageId: string;
  webUrl: string;
}

export interface EmailSent {
  messageId: string;
  threadId: string;
  webUrl: string;
}

const SUBMIT_AGENT_REQUEST = gql`
  mutation submitAgentRequest(
    $prompt: String!
    $conversationId: String
    $autoApprovedTools: [String!]
    $accountPickerResume: AccountPickerResumeInput
    $agentTemplateId: ID
    $attachedImages: [AssistantImageAttachmentInput!]
    $proposalDecision: ProposalDecisionInput
  ) {
    submitAgentRequest(
      prompt: $prompt
      conversationId: $conversationId
      autoApprovedTools: $autoApprovedTools
      accountPickerResume: $accountPickerResume
      agentTemplateId: $agentTemplateId
      attachedImages: $attachedImages
      proposalDecision: $proposalDecision
    ) {
      runId
      userId
      parentRunId
      kind
      status
      inputPrompt
      summary
      errorMessage
      createdAt
      updatedAt
      completedAt
      suggestResolve
    }
  }
`;

const SUBMIT_AGENT_REQUEST_FROM_SUGGESTION = gql`
  mutation submitAgentRequestFromSuggestion(
    $prompt: String!
    $conversationId: String
    $autoApprovedTools: [String!]
    $accountPickerResume: AccountPickerResumeInput
    $agentTemplateId: ID
    $attachedImages: [AssistantImageAttachmentInput!]
    $origin: AgentSubmitOrigin!
  ) {
    submitAgentRequest(
      prompt: $prompt
      conversationId: $conversationId
      autoApprovedTools: $autoApprovedTools
      accountPickerResume: $accountPickerResume
      agentTemplateId: $agentTemplateId
      attachedImages: $attachedImages
      origin: $origin
    ) {
      runId
      userId
      parentRunId
      kind
      status
      inputPrompt
      summary
      errorMessage
      createdAt
      updatedAt
      completedAt
      suggestResolve
    }
  }
`;

// Apollo Server's own validation error code — not an app-defined
// GraphQLErrorCode — raised when the API does not yet know the `origin`
// argument (a web deploy ahead of API-F.04).
const GRAPHQL_VALIDATION_FAILED_ERROR_CODE =
  'GRAPHQL_VALIDATION_FAILED' as const;

const GET_AGENT_RUN = gql`
  query agentRun($runId: String!) {
    agentRun(runId: $runId) {
      runId
      userId
      parentRunId
      kind
      status
      inputPrompt
      summary
      errorMessage
      createdAt
      updatedAt
      completedAt
      suggestResolve
    }
  }
`;

const GET_AGENT_MESSAGES = gql`
  query agentMessages($runId: String!) {
    agentMessages(runId: $runId) {
      runId
      msgIdx
      role
      content
      turn
      createdAt
    }
  }
`;

const GET_AGENT_RUNS = gql`
  query agentRuns($status: AgentRunStatus, $limit: Int) {
    agentRuns(status: $status, limit: $limit) {
      runId
      userId
      parentRunId
      kind
      status
      inputPrompt
      summary
      errorMessage
      createdAt
      updatedAt
      completedAt
      suggestResolve
    }
  }
`;

export const MY_AGENT_CONVERSATION = gql`
  query myAgentConversation($messageLimit: Int) {
    myAgentConversation(messageLimit: $messageLimit) {
      conversation {
        conversationId
        userId
        title
        createdAt
        lastMessageAt
        messageCount
        lastRunId
        isSystem
        systemConversationKind
        pendingUserAction
        conversationKind
        sourceInboxEntryRef {
          taskGroupId
          entryId
        }
      }
      messages {
        runId
        msgIdx
        conversationId
        convMsgIdx
        role
        content
        turn
        createdAt
        subAgentId
        subAgentLabel
        agentTemplateId
        draftAction
        gmailDraftId
        gmailMessageId
        gmailWebUrl
        kind
        sourceFlowId
        sourceFlowName
        askUserPayload {
          executionId
          stepId
          options
          decision
          answeredAt
        }
        teamRequestPayload {
          requestId
          fromUserId
          fromUserName
          requestText
          suggestedText
          status
        }
        teamRequestReplyPayload {
          requestId
          fromUserName
          originalRequest
          attachments {
            kind
            sourceId
            title
            snapshot
          }
        }
        meetingPollInvitePayload {
          pollId
          title
          description
          durationMinutes
          initiatorName
          initiatorUserId
          slots {
            slotId
            start
            end
          }
          status
        }
        proposalDecision {
          proposalConvMsgIdx
          decision
          confirmedActionIds
          skippedActionIds
        }
        ledgerKind
        proposalStatus
        failureCode
        tier
        family
        expiresAt
        dismissedAt
        undoneAt
        settlesRef {
          conversationId
          runId
          msgIdx
        }
        settlesConvMsgIdx
        nextStepStatuses {
          nextStepId
          status
        }
        executorDirectiveKind
        replyWait {
          status
          waitingUntil
          recipients {
            platform
            label
            answered
          }
          replies {
            platform
            authorLabel
            receivedAt
            excerpt
          }
        }
      }
      activeRun {
        runId
        userId
        conversationId
        parentRunId
        kind
        status
        inputPrompt
        summary
        errorMessage
        createdAt
        updatedAt
        completedAt
        userStatusCreatedAt
        suggestResolve
      }
    }
  }
`;

interface SubmitAgentRequestResult {
  submitAgentRequest: AgentRun;
}

interface AgentRunResult {
  agentRun: AgentRun | null;
}

interface AgentMessagesResult {
  agentMessages: AgentMessage[];
}

interface AgentRunsResult {
  agentRuns: AgentRun[];
}

export interface MyAgentConversationResult {
  myAgentConversation: {
    conversation: AgentConversation | null;
    messages: AgentMessage[];
    activeRun: AgentRun | null;
  };
}

export interface AccountPickerResumeInput {
  accountEmail: string;
  originalListArgs: Record<string, unknown>;
}

interface SubmitAgentRequestVariables {
  prompt: string;
  conversationId?: string;
  autoApprovedTools?: string[];
  accountPickerResume?: AccountPickerResumeInput;
  agentTemplateId?: string;
  attachedImages?: AssistantImageAttachment[];
  proposalDecision?: ProposalDecisionRecord;
}

export interface SubmitAgentRequestOptions {
  origin?: AgentSubmitOrigin;
}

function isGraphqlValidationFailed(err: unknown): boolean {
  const container = err as {
    graphQLErrors?: { extensions?: { code?: string } }[];
  };
  return (
    container?.graphQLErrors?.[0]?.extensions?.code ===
    GRAPHQL_VALIDATION_FAILED_ERROR_CODE
  );
}

export function useSubmitAgentRequest() {
  const [mutate, { loading, error }] =
    useMutation<SubmitAgentRequestResult>(SUBMIT_AGENT_REQUEST);
  const [mutateFromSuggestion] = useMutation<SubmitAgentRequestResult>(
    SUBMIT_AGENT_REQUEST_FROM_SUGGESTION,
  );

  const submitAgentRequest = useCallback(
    async (
      variables: { variables: SubmitAgentRequestVariables },
      options?: SubmitAgentRequestOptions,
    ) => {
      if (options?.origin !== AgentSubmitOrigin.SUGGESTION_CHIP) {
        return mutate(variables);
      }
      try {
        return await mutateFromSuggestion({
          variables: { ...variables.variables, origin: options.origin },
        });
      } catch (err) {
        if (!isGraphqlValidationFailed(err)) {
          throw err;
        }
        log.info(
          '[agent.service] submitAgentRequestFromSuggestion rejected by an API that does not know the origin argument yet; retrying without it',
        );
        return mutate(variables);
      }
    },
    [mutate, mutateFromSuggestion],
  );

  return { submitAgentRequest, loading, error };
}

export function useGetAgentRun(runId: string | null) {
  const { data, loading, error } = useQuery<AgentRunResult>(GET_AGENT_RUN, {
    variables: { runId },
    skip: !runId,
  });

  return {
    run: data?.agentRun ?? null,
    loading,
    error: error ?? null,
  };
}

export function useGetAgentMessages(runId: string | null) {
  const { data, loading, error } = useQuery<AgentMessagesResult>(
    GET_AGENT_MESSAGES,
    {
      variables: { runId },
      skip: !runId,
    },
  );

  return {
    messages: data?.agentMessages ?? [],
    loading,
    error: error ?? null,
  };
}

export function useGetAgentRuns(status?: AgentRunStatus, limit?: number) {
  const { data, loading, error } = useQuery<AgentRunsResult>(GET_AGENT_RUNS, {
    variables: { status, limit },
  });

  return {
    runs: data?.agentRuns ?? [],
    loading,
    error: error ?? null,
  };
}

const CLEAR_MY_CONVERSATION = gql`
  mutation clearMyConversation {
    clearMyConversation {
      conversationId
      userId
      title
      createdAt
      lastMessageAt
      messageCount
      lastRunId
      archivedAt
    }
  }
`;

interface ClearMyConversationResult {
  clearMyConversation: AgentConversation;
}

export function useClearMyConversation() {
  const [mutate, { loading, error }] = useMutation<ClearMyConversationResult>(
    CLEAR_MY_CONVERSATION,
  );

  const clearConversation = useCallback(async () => {
    await mutate();
  }, [mutate]);

  return { clearConversation, loading, error };
}

const CLEAR_AGENT_CONVERSATION = gql`
  mutation clearAgentConversation($conversationId: ID!) {
    clearAgentConversation(conversationId: $conversationId) {
      conversationId
      userId
      title
      createdAt
      lastMessageAt
      messageCount
      lastRunId
      archivedAt
    }
  }
`;

interface ClearAgentConversationResult {
  clearAgentConversation: AgentConversation;
}

export function useClearAgentConversation() {
  const [mutate, { loading, error }] =
    useMutation<ClearAgentConversationResult>(CLEAR_AGENT_CONVERSATION);

  const clearConversation = useCallback(
    async (conversationId: string) => {
      await mutate({ variables: { conversationId } });
    },
    [mutate],
  );

  return { clearConversation, loading, error };
}

const CREATE_EMAIL_DRAFT = gql`
  mutation createEmailDraft($input: CreateEmailDraftInput!, $convMsgIdx: String) {
    createEmailDraft(input: $input, convMsgIdx: $convMsgIdx) {
      draftId
      messageId
      webUrl
    }
  }
`;

const SEND_EMAIL = gql`
  mutation sendEmail($input: SendEmailInput!, $convMsgIdx: String) {
    sendEmail(input: $input, convMsgIdx: $convMsgIdx) {
      messageId
      threadId
      webUrl
    }
  }
`;

const DISCARD_DRAFT = gql`
  mutation discardDraft($convMsgIdx: String!) {
    discardDraft(convMsgIdx: $convMsgIdx) {
      runId
      msgIdx
      convMsgIdx
      draftAction
    }
  }
`;

const MARK_SLACK_REPLY_SENT = gql`
  mutation markSlackReplySent($convMsgIdx: String!) {
    markSlackReplySent(convMsgIdx: $convMsgIdx) {
      runId
      msgIdx
      convMsgIdx
      draftAction
    }
  }
`;

interface CreateEmailDraftResult {
  createEmailDraft: EmailDraftCreated;
}

interface SendEmailResult {
  sendEmail: EmailSent;
}

interface DiscardDraftResult {
  discardDraft: {
    runId: string;
    msgIdx: string;
    convMsgIdx: string;
    draftAction: string;
  };
}

interface MarkSlackReplySentResult {
  markSlackReplySent: {
    runId: string;
    msgIdx: string;
    convMsgIdx: string;
    draftAction: string;
  };
}

export interface CreateEmailDraftArgs {
  input: CreateEmailDraftInput;
  convMsgIdx?: string;
}

export interface SendEmailArgs {
  input: SendEmailInput;
  convMsgIdx?: string;
}

export function useCreateEmailDraft() {
  const [mutate, { loading, error }] =
    useMutation<CreateEmailDraftResult>(CREATE_EMAIL_DRAFT);

  const createEmailDraft = useCallback(
    async ({ input, convMsgIdx }: CreateEmailDraftArgs) => {
      const { data } = await mutate({ variables: { input, convMsgIdx } });
      return data?.createEmailDraft ?? null;
    },
    [mutate],
  );

  return { createEmailDraft, loading, error };
}

export function useSendEmail() {
  const [mutate, { loading, error }] = useMutation<SendEmailResult>(SEND_EMAIL);

  const sendEmail = useCallback(
    async ({ input, convMsgIdx }: SendEmailArgs) => {
      const { data } = await mutate({ variables: { input, convMsgIdx } });
      return data?.sendEmail ?? null;
    },
    [mutate],
  );

  return { sendEmail, loading, error };
}

export function useDiscardDraft() {
  const [mutate, { loading, error }] =
    useMutation<DiscardDraftResult>(DISCARD_DRAFT);

  const discardDraft = useCallback(
    async (convMsgIdx: string) => {
      const { data } = await mutate({ variables: { convMsgIdx } });
      return data?.discardDraft ?? null;
    },
    [mutate],
  );

  return { discardDraft, loading, error };
}

export function useMarkSlackReplySent() {
  const [mutate, { loading, error }] = useMutation<MarkSlackReplySentResult>(
    MARK_SLACK_REPLY_SENT,
    {
      refetchQueries: ['myAgentConversation'],
    },
  );

  const markSlackReplySent = useCallback(
    async (convMsgIdx: string) => {
      const { data } = await mutate({ variables: { convMsgIdx } });
      return data?.markSlackReplySent ?? null;
    },
    [mutate],
  );

  return { markSlackReplySent, loading, error };
}

const STOP_AGENT_RUN = gql`
  mutation stopAgentRun($input: StopAgentRunInput!) {
    stopAgentRun(input: $input) {
      run {
        runId
        userId
        conversationId
        parentRunId
        kind
        status
        inputPrompt
        summary
        errorMessage
        createdAt
        updatedAt
        completedAt
        userStatusCreatedAt
        suggestResolve
      }
    }
  }
`;

interface StopAgentRunInput {
  runId: string;
}

interface StopAgentRunResult {
  stopAgentRun: {
    run: AgentRun;
  };
}

export function useStopAgentRun() {
  const [mutate, { loading, error }] =
    useMutation<StopAgentRunResult>(STOP_AGENT_RUN);

  const stopAgentRun = useCallback(
    async (runId: string) => {
      const { data } = await mutate({
        variables: { input: { runId } satisfies StopAgentRunInput },
      });
      return data?.stopAgentRun.run ?? null;
    },
    [mutate],
  );

  return { stopAgentRun, loading, error };
}

const DEFAULT_MESSAGE_LIMIT = 50 as const;

export interface UseGetMyAgentConversationOptions {
  skip?: boolean;
}

export function useGetMyAgentConversation(
  options?: UseGetMyAgentConversationOptions,
) {
  const skipQuery = options?.skip === true;
  const { data, loading, error, refetch } = useQuery<MyAgentConversationResult>(
    MY_AGENT_CONVERSATION,
    {
      variables: { messageLimit: DEFAULT_MESSAGE_LIMIT },
      fetchPolicy: 'network-only',
      skip: skipQuery,
    },
  );

  return useMemo(
    () => ({
      conversation: data?.myAgentConversation?.conversation ?? null,
      messages: data?.myAgentConversation?.messages ?? EMPTY_LIST,
      activeRun: data?.myAgentConversation?.activeRun ?? null,
      loading,
      error: error ?? null,
      refetch,
    }),
    [data, loading, error, refetch],
  );
}

export const MY_AGENT_CONVERSATION_BY_ID_QUERY = gql`
  query myAgentConversationById($conversationId: ID!, $messageLimit: Int) {
    myAgentConversation(conversationId: $conversationId, messageLimit: $messageLimit) {
      conversation {
        conversationId
        userId
        title
        createdAt
        lastMessageAt
        messageCount
        lastRunId
        archivedAt
        isSystem
        systemConversationKind
        pendingUserAction
        conversationKind
        sourceInboxEntryRef {
          taskGroupId
          entryId
        }
      }
      messages {
        runId
        msgIdx
        conversationId
        convMsgIdx
        role
        content
        turn
        createdAt
        subAgentId
        subAgentLabel
        agentTemplateId
        draftAction
        gmailDraftId
        gmailMessageId
        gmailWebUrl
        kind
        sourceFlowId
        sourceFlowName
        askUserPayload {
          executionId
          stepId
          options
          decision
          answeredAt
        }
        teamRequestPayload {
          requestId
          fromUserId
          fromUserName
          requestText
          suggestedText
          status
        }
        teamRequestReplyPayload {
          requestId
          fromUserName
          originalRequest
          attachments {
            kind
            sourceId
            title
            snapshot
          }
        }
        meetingPollInvitePayload {
          pollId
          title
          description
          durationMinutes
          initiatorName
          initiatorUserId
          slots {
            slotId
            start
            end
          }
          status
        }
        proposalDecision {
          proposalConvMsgIdx
          decision
          confirmedActionIds
          skippedActionIds
        }
        ledgerKind
        proposalStatus
        failureCode
        tier
        family
        expiresAt
        dismissedAt
        undoneAt
        settlesRef {
          conversationId
          runId
          msgIdx
        }
        settlesConvMsgIdx
        nextStepStatuses {
          nextStepId
          status
        }
        executorDirectiveKind
        replyWait {
          status
          waitingUntil
          recipients {
            platform
            label
            answered
          }
          replies {
            platform
            authorLabel
            receivedAt
            excerpt
          }
        }
      }
      activeRun {
        runId
        userId
        conversationId
        parentRunId
        kind
        status
        inputPrompt
        summary
        errorMessage
        createdAt
        updatedAt
        completedAt
        userStatusCreatedAt
        suggestResolve
      }
    }
  }
`;

export interface MyAgentConversationByIdResult {
  myAgentConversation: {
    conversation: AgentConversation | null;
    messages: AgentMessage[];
    activeRun: AgentRun | null;
  };
}

interface MyAgentConversationByIdVariables {
  conversationId: string;
  messageLimit?: number;
}

export function useGetMyAgentConversationById(
  conversationId: string | null,
  skip?: boolean,
) {
  const { data, loading, error, refetch } =
    useQuery<MyAgentConversationByIdResult>(MY_AGENT_CONVERSATION_BY_ID_QUERY, {
      variables: {
        conversationId: conversationId ?? '',
        messageLimit: DEFAULT_MESSAGE_LIMIT,
      } satisfies MyAgentConversationByIdVariables,
      skip: skip === true || conversationId === null,
      fetchPolicy: 'network-only',
    });

  return useMemo(
    () => ({
      conversation: data?.myAgentConversation?.conversation ?? null,
      messages: data?.myAgentConversation?.messages ?? EMPTY_LIST,
      activeRun: data?.myAgentConversation?.activeRun ?? null,
      loading,
      error: error ?? null,
      refetch,
    }),
    [data, loading, error, refetch],
  );
}

export const MY_AGENT_CONVERSATIONS = gql`
  query myAgentConversations($includeArchived: Boolean, $limit: Int) {
    myAgentConversations(includeArchived: $includeArchived, limit: $limit) {
      conversation {
        conversationId
        userId
        title
        createdAt
        lastMessageAt
        messageCount
        lastRunId
        archivedAt
        isSystem
        systemConversationKind
        pendingUserAction
        conversationKind
        sourceInboxEntryRef {
          taskGroupId
          entryId
        }
      }
      activeRun {
        runId
        userId
        conversationId
        parentRunId
        kind
        status
        inputPrompt
        summary
        errorMessage
        createdAt
        updatedAt
        completedAt
        userStatusCreatedAt
        suggestResolve
      }
    }
  }
`;

export interface AgentConversationListItem {
  conversation: AgentConversation;
  activeRun: AgentRun | null;
}

export interface MyAgentConversationsResult {
  myAgentConversations: AgentConversationListItem[];
}

interface MyAgentConversationsVariables {
  includeArchived?: boolean;
  limit?: number;
}

const CONVERSATIONS_DEFAULT_LIMIT = 50 as const;

export function useGetMyAgentConversations(includeArchived = false) {
  const { data, loading, error, refetch } =
    useQuery<MyAgentConversationsResult>(MY_AGENT_CONVERSATIONS, {
      variables: {
        includeArchived,
        limit: CONVERSATIONS_DEFAULT_LIMIT,
      } satisfies MyAgentConversationsVariables,
      fetchPolicy: 'network-only',
    });

  return useMemo(
    () => ({
      items: data?.myAgentConversations ?? EMPTY_LIST,
      loading,
      error: error ?? null,
      refetch,
    }),
    [data, loading, error, refetch],
  );
}

export const MY_PENDING_USER_ACTION_CONVERSATION_IDS = gql`
  query MyPendingUserActionConversationIds {
    myPendingUserActionConversationIds
  }
`;

export interface MyPendingUserActionConversationIdsResult {
  myPendingUserActionConversationIds: string[];
}

export const CREATE_AGENT_CONVERSATION = gql`
  mutation createAgentConversation($title: String) {
    createAgentConversation(title: $title) {
      conversationId
      userId
      title
      createdAt
      lastMessageAt
      messageCount
      lastRunId
      archivedAt
      conversationKind
      sourceInboxEntryRef {
        taskGroupId
        entryId
      }
    }
  }
`;

interface CreateAgentConversationResult {
  createAgentConversation: AgentConversation;
}

export function useCreateAgentConversation() {
  const [mutate, { loading, error }] =
    useMutation<CreateAgentConversationResult>(CREATE_AGENT_CONVERSATION);

  const createAgentConversation = useCallback(
    async (title?: string) => {
      const { data } = await mutate({ variables: { title } });
      return data?.createAgentConversation ?? null;
    },
    [mutate],
  );

  return { createAgentConversation, loading, error };
}

const CLOSE_AGENT_CONVERSATION = gql`
  mutation closeAgentConversation($conversationId: ID!) {
    closeAgentConversation(conversationId: $conversationId) {
      conversationId
      userId
      title
      createdAt
      lastMessageAt
      messageCount
      lastRunId
      archivedAt
    }
  }
`;

interface CloseAgentConversationResult {
  closeAgentConversation: AgentConversation;
}

export function useCloseAgentConversation() {
  const [mutate, { loading, error }] =
    useMutation<CloseAgentConversationResult>(CLOSE_AGENT_CONVERSATION);

  const closeAgentConversation = useCallback(
    async (conversationId: string) => {
      const { data } = await mutate({ variables: { conversationId } });
      return data?.closeAgentConversation ?? null;
    },
    [mutate],
  );

  return { closeAgentConversation, loading, error };
}

const REOPEN_AGENT_CONVERSATION = gql`
  mutation reopenAgentConversation($conversationId: ID!) {
    reopenAgentConversation(conversationId: $conversationId) {
      conversationId
      userId
      title
      createdAt
      lastMessageAt
      messageCount
      lastRunId
      archivedAt
    }
  }
`;

interface ReopenAgentConversationResult {
  reopenAgentConversation: AgentConversation;
}

export function useReopenAgentConversation() {
  const [mutate, { loading, error }] =
    useMutation<ReopenAgentConversationResult>(REOPEN_AGENT_CONVERSATION);

  const reopenAgentConversation = useCallback(
    async (conversationId: string) => {
      const { data } = await mutate({ variables: { conversationId } });
      return data?.reopenAgentConversation ?? null;
    },
    [mutate],
  );

  return { reopenAgentConversation, loading, error };
}

const RENAME_AGENT_CONVERSATION = gql`
  mutation renameAgentConversation($conversationId: ID!, $title: String!) {
    renameAgentConversation(conversationId: $conversationId, title: $title) {
      conversationId
      userId
      title
      createdAt
      lastMessageAt
      messageCount
      lastRunId
      archivedAt
    }
  }
`;

interface RenameAgentConversationResult {
  renameAgentConversation: AgentConversation;
}

export function useRenameAgentConversation() {
  const [mutate, { loading, error }] =
    useMutation<RenameAgentConversationResult>(RENAME_AGENT_CONVERSATION);

  const renameAgentConversation = useCallback(
    async (conversationId: string, title: string) => {
      const { data } = await mutate({ variables: { conversationId, title } });
      return data?.renameAgentConversation ?? null;
    },
    [mutate],
  );

  return { renameAgentConversation, loading, error };
}

const DELETE_AGENT_CONVERSATION = gql`
  mutation deleteAgentConversation($conversationId: ID!) {
    deleteAgentConversation(conversationId: $conversationId)
  }
`;

interface DeleteAgentConversationResult {
  deleteAgentConversation: boolean;
}

export function useDeleteAgentConversation() {
  const [mutate, { loading, error }] =
    useMutation<DeleteAgentConversationResult>(DELETE_AGENT_CONVERSATION);

  const deleteAgentConversation = useCallback(
    async (conversationId: string): Promise<boolean> => {
      const { data } = await mutate({ variables: { conversationId } });
      return data?.deleteAgentConversation ?? false;
    },
    [mutate],
  );

  return { deleteAgentConversation, loading, error };
}

const RESPOND_TO_FLOW_ASK_USER = gql`
  mutation respondToFlowAskUser(
    $executionId: ID!
    $decision: String!
    $pauseStepId: String
  ) {
    respondToFlowAskUser(
      executionId: $executionId
      decision: $decision
      pauseStepId: $pauseStepId
    ) {
      executionId
      status
    }
  }
`;

interface RespondToFlowAskUserResult {
  respondToFlowAskUser: { executionId: string; status: string };
}

interface RespondToFlowAskUserVariables {
  executionId: string;
  decision: string;
  pauseStepId?: string;
}

export interface TeamRequestAttachmentInput {
  kind: string;
  sourceId: string;
  title: string;
}

export interface RespondToTeamRequestVariables {
  requestId: string;
  decision: string;
  message?: string;
  attachments?: TeamRequestAttachmentInput[];
}

const RESPOND_TO_TEAM_REQUEST = gql`
  mutation respondToTeamRequest(
    $requestId: ID!
    $decision: TeamRequestDecision!
    $message: String
    $attachments: [TeamRequestAttachmentInput!]
  ) {
    respondToTeamRequest(
      requestId: $requestId
      decision: $decision
      message: $message
      attachments: $attachments
    ) {
      requestId
      status
    }
  }
`;

interface RespondToTeamRequestResult {
  respondToTeamRequest: { requestId: string; status: string };
}

export function useRespondToTeamRequest() {
  const [mutate, { loading }] = useMutation<RespondToTeamRequestResult>(
    RESPOND_TO_TEAM_REQUEST,
  );

  const respondToTeamRequest = useCallback(
    async (
      variables: RespondToTeamRequestVariables,
      optimisticUpdate?: () => void,
      rollback?: () => void,
    ) => {
      if (optimisticUpdate) {
        optimisticUpdate();
      }
      try {
        await mutate({ variables });
      } catch (error) {
        if (rollback) {
          rollback();
        }
        throw error;
      }
    },
    [mutate],
  );

  return { respondToTeamRequest, loading };
}

const ACKNOWLEDGE_CONVERSATION = gql`
  mutation acknowledgeConversation($conversationId: ID!) {
    acknowledgeConversation(conversationId: $conversationId)
  }
`;

interface AcknowledgeConversationResult {
  acknowledgeConversation: boolean;
}

export function useAcknowledgeConversation() {
  const [mutate] = useMutation<AcknowledgeConversationResult>(
    ACKNOWLEDGE_CONVERSATION,
  );

  const acknowledgeConversation = useCallback(
    (conversationId: string) => {
      mutate({ variables: { conversationId } }).catch(() => undefined);
    },
    [mutate],
  );

  return { acknowledgeConversation };
}

export function useRespondToFlowAskUser() {
  const [mutate, { loading }] = useMutation<RespondToFlowAskUserResult>(
    RESPOND_TO_FLOW_ASK_USER,
  );

  const respondToFlowAskUser = useCallback(
    async (
      executionId: string,
      decision: string,
      optimisticUpdate?: () => void,
      rollback?: () => void,
      pauseStepId?: string,
    ) => {
      if (optimisticUpdate) {
        optimisticUpdate();
      }
      try {
        await mutate({
          variables: {
            executionId,
            decision,
            pauseStepId,
          } satisfies RespondToFlowAskUserVariables,
        });
      } catch (error) {
        if (rollback) {
          rollback();
        }
        throw error;
      }
    },
    [mutate],
  );

  return { respondToFlowAskUser, loading };
}

const RESPOND_TO_MEETING_POLL = gql`
  mutation respondToMeetingPoll(
    $pollId: ID!
    $slotId: ID
    $declined: Boolean
  ) {
    respondToMeetingPoll(pollId: $pollId, slotId: $slotId, declined: $declined) {
      pollId
      status
      selectedSlotId
    }
  }
`;

interface RespondToMeetingPollResult {
  respondToMeetingPoll: {
    pollId: string;
    status: string;
    selectedSlotId: string | null;
  };
}

export interface RespondToMeetingPollVariables {
  pollId: string;
  slotId?: string;
  declined?: boolean;
}

export function useRespondToMeetingPoll() {
  const [mutate, { loading }] = useMutation<RespondToMeetingPollResult>(
    RESPOND_TO_MEETING_POLL,
  );

  const respondToMeetingPoll = useCallback(
    async (
      variables: RespondToMeetingPollVariables,
      optimisticUpdate?: () => void,
      rollback?: () => void,
    ) => {
      if (optimisticUpdate) {
        optimisticUpdate();
      }
      try {
        await mutate({ variables });
      } catch (error) {
        if (rollback) {
          rollback();
        }
        throw error;
      }
    },
    [mutate],
  );

  return { respondToMeetingPoll, loading };
}

const UNDO_DELEGATED_SEND = gql`
  mutation undoDelegatedSend($runId: String!, $undo: DelegatedSendUndoInput!) {
    undoDelegatedSend(runId: $runId, undo: $undo)
  }
`;

interface UndoDelegatedSendResult {
  undoDelegatedSend: boolean;
}

export function useUndoDelegatedSend() {
  const [mutate, { loading, error }] =
    useMutation<UndoDelegatedSendResult>(UNDO_DELEGATED_SEND);

  const undoDelegatedSend = useCallback(
    async (runId: string, undo: DelegatedSendUndoHandle) => {
      const { data } = await mutate({ variables: { runId, undo } });
      return data?.undoDelegatedSend ?? false;
    },
    [mutate],
  );

  return { undoDelegatedSend, loading, error };
}
